import { API_BASE_URL } from '../../../lib/apiBaseUrl';

const buildUrl = (path, params) => {
    const url = new URL(`${API_BASE_URL}${path}`);
    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') return;
            url.searchParams.set(key, String(value));
        });
    }
    return url.toString();
};

const waitForCurrentUser = (firebase, timeoutMs = 10000) =>
    new Promise((resolve, reject) => {
        const auth = firebase?.auth;
        if (!auth?.onAuthStateChanged) {
            reject(new Error('Firebase auth is not available.'));
            return;
        }

        if (auth.currentUser) {
            resolve(auth.currentUser);
            return;
        }

        const timer = setTimeout(() => {
            unsubscribe?.();
            reject(new Error('No signed-in Firebase user found.'));
        }, timeoutMs);

        const unsubscribe = auth.onAuthStateChanged(
            (user) => {
                if (!user) return;
                clearTimeout(timer);
                unsubscribe();
                resolve(user);
            },
            (error) => {
                clearTimeout(timer);
                unsubscribe();
                reject(error);
            },
        );
    });

const getIdToken = async (firebase) => {
    const currentUser = firebase?.auth?.currentUser || (await waitForCurrentUser(firebase));
    return currentUser.getIdToken();
};

const parseError = async (response) => {
    try {
        const data = await response.json();
        return data;
    } catch (_err) {
        return { error: response.statusText };
    }
};

const apiFetch = async (firebase, path, options = {}, params) => {
    const token = await getIdToken(firebase);
    const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
    };

    let response;
    try {
        response = await fetch(buildUrl(path, params), {
            ...options,
            headers,
        });
    } catch (err) {
        const error = new Error(
            `Cannot reach Templates backend at ${API_BASE_URL}. Make sure the server is running.`,
        );
        error.code = 'TEMPLATES_BACKEND_UNREACHABLE';
        error.status = 0;
        error.cause = err;
        throw error;
    }

    if (!response.ok) {
        const errorPayload = await parseError(response);
        const error = new Error(errorPayload.error || 'Templates API request failed.');
        error.code = errorPayload.code || 'TEMPLATES_API_ERROR';
        error.status = response.status;
        throw error;
    }

    if (response.status === 204) return null;
    return response.json();
};

// ─────────────────────────────────────────────────────────────────────────────
// Templates API
// ─────────────────────────────────────────────────────────────────────────────

export const templatesList = async (firebase, params = {}) =>
    apiFetch(firebase, '/api/templates', { method: 'GET' }, params);

export const templatesGet = async (firebase, templateId) =>
    apiFetch(firebase, `/api/templates/${templateId}`, { method: 'GET' });

export const templatesCreate = async (firebase, templateId, data) =>
    apiFetch(firebase, '/api/templates', {
        method: 'POST',
        body: JSON.stringify({ templateId, data }),
    });

export const templatesUpdate = async (firebase, templateId, data) =>
    apiFetch(firebase, `/api/templates/${templateId}`, {
        method: 'PATCH',
        body: JSON.stringify({ data }),
    });

export const templatesDelete = async (firebase, templateId) =>
    apiFetch(firebase, `/api/templates/${templateId}`, { method: 'DELETE' });

// ─────────────────────────────────────────────────────────────────────────────
// Documents API
// ─────────────────────────────────────────────────────────────────────────────

export const documentsList = async (firebase, params = {}) =>
    apiFetch(firebase, '/api/documents', { method: 'GET' }, params);

export const documentsGet = async (firebase, documentId) =>
    apiFetch(firebase, `/api/documents/${documentId}`, { method: 'GET' });

export const documentsCreate = async (firebase, documentId, data) =>
    apiFetch(firebase, '/api/documents', {
        method: 'POST',
        body: JSON.stringify({ documentId, data }),
    });

export const documentsDelete = async (firebase, documentId) =>
    apiFetch(firebase, `/api/documents/${documentId}`, { method: 'DELETE' });
